import { isRecorderUiEnabled } from '../recording/recorderBuildPolicy';
import { createClient } from '@connectrpc/connect';
import { RevitService } from '../../shared/api/grpc/gen/revit_service_pb';
import { getGrpcTransport } from '../../shared/api/grpc/transport';
import { useAuthStore } from '../../shared/stores/useAuthStore';
import {
  downloadScenarioArchive,
  type ScenarioArchiveInput,
  type ScenarioElementArtifact,
} from '../archive';
import { createGrpcModelTransport } from '../model/grpcModelTransport';
import { gatherModelArtifact } from '../model/modelArtifactGatherer';
import { recorderDiagnosticsLog } from '../recording/recorderDiagnosticsLog';
import {
  setScenarioArchiveDownloader,
  type RecorderPrivacyOptions,
} from '../recording/useRecorderStore';
import type { RecorderSnapshot } from '../recording/scenarioRecorder';
import {
  collectEnvironment,
  collectFeatureFlags,
  collectWebSettings,
  toEnvironmentInfo,
} from '../settings/settingsCollector';

const WEB_CLIENT_VERSION = '0.1.0';


function client() {
  const transport = getGrpcTransport();
  if (!transport) {
    return null;
  }
  return createClient(RevitService, transport);
}

function newId(): string {
  const maybeCrypto = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (maybeCrypto?.randomUUID) {
    return maybeCrypto.randomUUID();
  }
  return `scenario-${Date.now().toString(36)}`;
}

function safeId(uniqueId: string): string {
  return uniqueId ? uniqueId.replace(/[^A-Za-z0-9._-]/g, '_') : 'unknown';
}

function omitEmpty<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null && v !== '') {
      out[k] = v;
    }
  }
  return out as Partial<T>;
}

/** Pull element + parameter snapshots for the selection and project them to archive artifacts. */
async function collectElementArtifacts(uniqueIds: string[]): Promise<ScenarioElementArtifact[]> {
  const c = client();
  if (!c || uniqueIds.length === 0) {
    return [];
  }
  const res = await c.getElementSnapshots({ uniqueIds });
  if (!res.isSuccess) {
    recorderDiagnosticsLog.dataCollectionError('GetElementSnapshots failed', { detail: res.error });
    return [];
  }

  return res.elements.map((el) => {
    const id = safeId(el.uniqueId);
    const meta = omitEmpty({
      uniqueId: el.uniqueId,
      elementId: el.elementId !== undefined ? Number(el.elementId) : undefined,
      isResolved: el.isResolved,
      warning: el.warning,
      category: el.category,
      familyName: el.familyName,
      typeName: el.typeName,
      hostUniqueId: el.hostUniqueId,
      level: el.level,
      workset: el.workset,
      phase: el.phase,
      designOption: el.designOption,
      boundingBox: el.boundingBox,
      location: el.location,
      relatedElements: el.relatedElements?.length
        ? el.relatedElements.map((r) => ({ uniqueId: r.uniqueId, relation: r.relation }))
        : undefined,
    });
    const parameters = {
      uniqueId: el.uniqueId,
      instanceParameters: el.instanceParameters ?? [],
      typeParameters: el.typeParameters ?? [],
    };
    return {
      element: {
        uniqueId: el.uniqueId,
        elementId: el.elementId !== undefined ? Number(el.elementId) : undefined,
        category: el.category || undefined,
        parametersRef: `elements/${id}.parameters.json`,
      },
      elementRef: `elements/${id}.element.json`,
      elementJson: JSON.stringify(meta, null, 2),
      parametersJson: JSON.stringify(parameters, null, 2),
    };
  });
}

/**
 * Assemble a full {@link ScenarioArchiveInput} from the live plugin (gRPC) +
 * the in-memory recorder snapshot: document/view/selection context, element +
 * parameter snapshots, plugin settings, the bundled model copy (chunk-pulled),
 * and the diagnostics log. Reuses the team's gatherers.
 */
export async function collectScenarioArchiveInput(
  snapshot: RecorderSnapshot,
  meta: { name: string; description?: string },
  privacy: RecorderPrivacyOptions,
): Promise<ScenarioArchiveInput> {
  const c = client();

  // Document / view / selection context.
  let documentTitle = '';
  let revitVersion: string | undefined;
  let selection: string[] = [];
  if (c) {
    try {
      const ctx = await c.getRecorderContext({});
      if (ctx.isSuccess && ctx.hasActiveDocument) {
        documentTitle = ctx.document?.title ?? '';
        revitVersion = ctx.document?.revitVersion || undefined;
        selection = ctx.selection.map((s) => s.uniqueId).filter(Boolean);
      }
    } catch (err) {
      recorderDiagnosticsLog.revitApiError('GetRecorderContext failed', {
        detail: err instanceof Error ? err.message : String(err),
      });
    }
  }

  // Plugin settings (no model staging on this call).
  let pluginSettingsJson: string | undefined;
  let pluginVersion: string | undefined;
  if (c) {
    try {
      const exp = await c.getRecorderExport({ includeModel: false });
      if (exp.isSuccess && exp.settingsJson) {
        pluginSettingsJson = exp.settingsJson;
        try {
          pluginVersion = JSON.parse(exp.settingsJson)?.pluginVersion;
        } catch {
          /* settings JSON unparseable — leave version unknown */
        }
      }
    } catch (err) {
      recorderDiagnosticsLog.dataCollectionError('GetRecorderExport (settings) failed', {
        detail: err instanceof Error ? err.message : String(err),
      });
    }
  }

  // Model copy (chunk-pulled) — skipped when the privacy toggle disables it.
  let model: ScenarioArchiveInput['model'];
  let modelReferenceVersion: string | undefined;
  if (c) {
    try {
      const gathered = await gatherModelArtifact(createGrpcModelTransport(), !privacy.disableModelSave);
      model = gathered.model;
      modelReferenceVersion = gathered.reference.revitVersion;
      if (gathered.warning) {
        recorderDiagnosticsLog.collectionWarning(gathered.warning);
      }
    } catch (err) {
      recorderDiagnosticsLog.fileSaveError('model/test-model.rvt', err instanceof Error ? err.message : String(err));
    }
  }

  const elements = await collectElementArtifacts(selection);

  const environment = toEnvironmentInfo(
    collectEnvironment({
      appVersion: pluginVersion,
      webClientVersion: WEB_CLIENT_VERSION,
      revitPluginVersion: pluginVersion,
      revitVersion: revitVersion ?? modelReferenceVersion,
      os: typeof navigator !== 'undefined' ? navigator.platform : undefined,
    }),
  );

  const user = useAuthStore.getState().user;

  return {
    scenario: {
      id: newId(),
      name: meta.name,
      createdAt: new Date().toISOString(),
      createdBy: user?.username || 'unknown',
      description: meta.description,
    },
    environment,
    mode: snapshot.mode,
    snapshot,
    revit: documentTitle ? { document: { title: documentTitle }, selection } : undefined,
    elements,
    pluginSettingsJson,
    webSettings: collectWebSettings({}),
    featureFlags: collectFeatureFlags({}),
    logLines: recorderDiagnosticsLog.lines(),
    model,
  };
}

let registered = false;

/** Wire the full archive collector into the recorder's download seam (idempotent). */
export function registerRecorderArchiveDownloader(): void {
  if (registered || !isRecorderUiEnabled()) {
    return;
  }
  registered = true;
  setScenarioArchiveDownloader(async (snapshot, meta, privacy) => {
    const effectivePrivacy = privacy ?? {
      excludeAbsolutePaths: false,
      maskLocalDirectories: false,
      maskUsername: false,
      disableModelSave: false,
      disableBigPayloadSave: false,
    };
    const input = await collectScenarioArchiveInput(snapshot, meta, effectivePrivacy);
    // allowPartial: a recording stopped mid-failure still yields a flagged archive.
    downloadScenarioArchive(input, { allowPartial: true, redaction: effectivePrivacy });
  });
}
